import { forwardRef, useCallback, useEffect, useMemo, useState } from "react";
import { IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { Icon } from "@iconify/react";
import fileExcel from "@iconify/icons-mdi/file-excel";
import { useTranslation } from "react-i18next";
import { ScheduleExcelExportDialog } from "./ScheduleExcelExportDialog.partial";
import { BookingScheduleInterface } from "../../../../features/Booking-Form/typings/booking.types";
import {
  buildCsvPrintData,
  ExcelData,
  ExcelHeadCell
} from "../../../FacilityManager/Hooks/useExcelPrint";
import { TeamMember } from "../../../../features/Booking-Form/typings/team-member";
import useTimeFormat from "../../../../hooks/useTimeFormat/useTimeFormat";
import { DateTime } from "luxon";
import {
  initDetailAddress,
  initFullName,
  transBookingType,
  transWeekday
} from "../functions/excel.export.functions";
import type { ScheduleExcelExportFetchConfig } from "../functions/schedule-excel-export-fetch.functions";
import {
  defaultExportPeriodIso,
  normalizeExportPeriodBounds,
  scheduleIntersectsRange,
  triggerCsvDownload
} from "../functions/schedule-excel-export.functions";
import { useScheduleExcelExportFetch } from "./useScheduleExcelExportFetch";
import {
  generateAddressDetail,
  isScheduleMobileWorkingLocation
} from "../../schedule-event-location.component";
import { IZoneInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/ZoneInventory.type";
import { IPlaceInventory } from "../../../FacilityManager/Domain/Types/FloorPlan/PlaceInventory.type";

type P = {
  isDisabled: boolean;
  users?: TeamMember[];
  fetchConfig: ScheduleExcelExportFetchConfig;
  /** When the dialog opens, prefill the period pickers from the visible calendar window (ISO strings). */
  initialExportRange?: { startDate: string; endDate: string } | null;
};

export const ScheduleExcelExport = forwardRef<HTMLSpanElement, P>((props, ref) => {
  const { users, isDisabled, fetchConfig, initialExportRange = null } = props;
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const iconColor = theme.palette.mode === "light" ? "black" : "white";

  const { dateFormat, timeFormat } = useTimeFormat();
  const localeFullFormat = dateFormat + " " + timeFormat;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [periodStart, setPeriodStart] = useState<string>("");
  const [periodEnd, setPeriodEnd] = useState<string>("");
  const [isExporting, setIsExporting] = useState(false);

  const { fetchSchedulesForPeriod } = useScheduleExcelExportFetch(fetchConfig);

  const bookingFileHeaders = useMemo(
    (): ExcelHeadCell<Record<string, unknown>>[] => [
      { key: "name", label: t("Name") },
      { key: "bookingType", label: t("Excel_BookingType") },
      { key: "location", label: t("Location") },
      { key: "floor", label: t("Floor") },
      { key: "detailAddress", label: t("Address") },
      { key: "bookingInventoryName", label: t("Resource Name") },
      { key: "title", label: t("Title") },
      { key: "weekday", label: t("Weekday") },
      { key: "startDate", label: t("Start Date") },
      { key: "endDate", label: t("End Date") }
    ],
    [t]
  );

  useEffect(() => {
    if (!dialogOpen) {
      return undefined;
    }
    if (initialExportRange?.startDate && initialExportRange?.endDate) {
      setPeriodStart(initialExportRange.startDate);
      setPeriodEnd(initialExportRange.endDate);
      return undefined;
    }
    const next = defaultExportPeriodIso();
    setPeriodStart(next.start);
    setPeriodEnd(next.end);
    return undefined;
  }, [dialogOpen, initialExportRange?.startDate, initialExportRange?.endDate]);

  const mapSchedulesToExcelData = useCallback(
    (list: BookingScheduleInterface[]): ExcelData<Record<string, unknown>> => ({
      excelHeaders: bookingFileHeaders,
      excelData: list.map(schedule => {
        const inv = schedule.bookingInventory as IPlaceInventory | IZoneInventory;
        const omitLocationDetails = isScheduleMobileWorkingLocation(schedule);
        return {
          name: initFullName(schedule, users, t),
          bookingType: transBookingType(schedule.bookingType, t),
          location: schedule.locationName,
          floor: omitLocationDetails ? "" : schedule.floorName,
          detailAddress: omitLocationDetails
            ? ""
            : initDetailAddress(generateAddressDetail(inv?.locationInventory, inv?.floorInventory)),
          bookingInventoryName: omitLocationDetails ? "" : schedule.bookingInventory?.name ?? "",
          title: schedule.subject ?? schedule.tripReason ?? "",
          weekday: transWeekday(DateTime.fromISO(schedule.startDate).weekdayLong, t),
          startDate: DateTime.fromISO(schedule.startDate).toFormat(localeFullFormat),
          endDate: DateTime.fromISO(schedule.endDate).toFormat(localeFullFormat)
        };
      })
    }),
    [bookingFileHeaders, localeFullFormat, t, users]
  );

  const rangeIsValid = useMemo(() => {
    if (periodStart.length === 0 || periodEnd.length === 0) {
      return false;
    }
    const start = DateTime.fromISO(periodStart).toLocal().startOf("day");
    const end = DateTime.fromISO(periodEnd).toLocal().startOf("day");
    return start.isValid && end.isValid && start <= end;
  }, [periodStart, periodEnd]);

  const handleConfirmExport = async () => {
    if (!rangeIsValid || isExporting) {
      return;
    }

    setIsExporting(true);
    try {
      const { rangeStart, rangeEnd, fetchStart, fetchEnd } = normalizeExportPeriodBounds({
        startDate: periodStart,
        endDate: periodEnd
      });

      const fetched = await fetchSchedulesForPeriod({
        startDate: fetchStart,
        endDate: fetchEnd
      });

      const filtered = fetched.filter(s => scheduleIntersectsRange(s, rangeStart, rangeEnd));

      const { CSVheaders, CSVData, separatorByLang } = buildCsvPrintData(
        mapSchedulesToExcelData(filtered),
        i18n.language
      );

      triggerCsvDownload(CSVheaders, CSVData, separatorByLang, t("Your Bookings"));
      setDialogOpen(false);
    } finally {
      setIsExporting(false);
    }
  };

  const handleRangeChange = (start: string | null, end: string | null) => {
    if (start != null) {
      setPeriodStart(start);
    }
    if (end != null) {
      setPeriodEnd(end);
    }
  };

  return (
    <>
      <Tooltip title={<Typography>{t("Export your bookings")}</Typography>}>
        <span ref={ref}>
          <IconButton
            data-testid="handle-excel-export-btn"
            sx={{ width: "48px", height: "46px", p: "4px 0px 0px 0px" }}
            disabled={isDisabled}
            onClick={() => setDialogOpen(true)}
          >
            <Icon icon={fileExcel} color={isDisabled ? "grey" : iconColor} width={24} height={24} />
          </IconButton>
        </span>
      </Tooltip>

      <ScheduleExcelExportDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        periodStart={periodStart}
        periodEnd={periodEnd}
        onRangeChange={handleRangeChange}
        rangeIsValid={rangeIsValid}
        isExporting={isExporting}
        onConfirm={handleConfirmExport}
      />
    </>
  );
});

ScheduleExcelExport.displayName = "ScheduleExcelExport";
